import type { UploadsAnyCreateRequest, UploadsFacadeClient } from "./uploadsFacadeClient";
import { parseUploadV2Snapshot, type UploadV2Snapshot, type UploadCapabilities } from "./uploadObjectContract";
import { buildKnownSizeUploadObject, firstUploadObjectDeclaration } from "./uploadObjectManifest";
import {
	parseUploadKnownSizeControl,
	uploadControlError,
	readUploadKnownSizeControl,
	type UploadObjectControlRow,
	type UploadKnownSizeControl
} from "./uploadKnownSizeControl";
import {
	createUploadsMultipartUploadTarget,
	persistedUploadTarget,
	type PersistedUploadTargetRow,
	type UploadTargetEnvelope,
	type UploadsMultipartUploadTarget
} from "./targetInitiation";
import { UploadAlreadyCompleteAtCreateError } from "./uploadFacadeErrors";
import { newUploadId } from "./uploadId";

function assertKnownSizeSeal(snapshot: UploadV2Snapshot, control: UploadKnownSizeControl): void {
	const summary = snapshot.sourceSeal?.sourceSummary as Record<string, unknown> | undefined;
	if (
		snapshot.sourceMode !== "known-size" ||
		snapshot.sourceSeal?.finalizationId !== control.finalizationId ||
		summary?.kind !== "known-size" ||
		summary.objectCount !== 1 ||
		summary.totalBytes !== control.object.byteSize
	)
		throw uploadControlError("Upload source seal differs from the saved object");
}
function assertTransferAllowed(snapshot: UploadV2Snapshot): void {
	if (snapshot.facts.terminalIntent === "discarded" || snapshot.facts.transferState === "cancelled")
		throw Object.assign(new Error("UPLOAD_ATTEMPT_FENCE_INVALID: source is no longer accepting upload work"), {
			code: "UPLOAD_ATTEMPT_FENCE_INVALID",
			status: 409
		});
}
export function throwUploadVerificationPending(): never {
	throw Object.assign(new Error("UPLOAD_VERIFICATION_PENDING: server verification is still in progress"), {
		code: "UPLOAD_VERIFICATION_PENDING",
		status: 503
	});
}
/** The caller must have committed control under its existing source/owner fence before entering. */
export async function bindKnownSizeUploadSource(
	client: UploadsFacadeClient,
	input: UploadsAnyCreateRequest,
	savedControl: UploadKnownSizeControl,
	signal?: AbortSignal
) {
	const control = parseUploadKnownSizeControl(savedControl);
	const created = await client.create(
		{
			...input,
			contractVersion: 2,
			sourceMode: "known-size",
			supportedTransports: ["chunked-object"],
			expectedBytes: control.object.byteSize,
			totalChunks: undefined,
			chunkManifest: undefined,
			finalizationId: control.finalizationId,
			objects: [firstUploadObjectDeclaration(control.object)]
		},
		signal
	);
	const snapshot = parseUploadV2Snapshot(created);
	if (snapshot.sourceMode !== "known-size") throw uploadControlError("Upload source mode changed");
	const objectId = created.objects![0].objectId;
	if (control.objectId && control.objectId !== objectId) throw uploadControlError("Saved upload object binding changed");
	if (snapshot.transferVerified) assertKnownSizeSeal(snapshot, control);
	return { snapshot, control: { ...control, objectId } };
}

/** Continue the existing driver's target initiation only after its source choice is durable. */
export async function createKnownSizeUploadTarget(
	client: UploadsFacadeClient,
	input: UploadsAnyCreateRequest,
	savedControl: UploadKnownSizeControl,
	signal?: AbortSignal,
	checkpoint?: (bound: Awaited<ReturnType<typeof bindKnownSizeUploadSource>>) => Promise<void>
): Promise<UploadsMultipartUploadTarget> {
	const admitted = await bindKnownSizeUploadSource(client, input, savedControl, signal);
	await checkpoint?.(admitted);
	const { snapshot, control: bound } = admitted;
	const control = bound;
	const objectId = bound.objectId;
	assertTransferAllowed(snapshot);
	if (snapshot.transferVerified) throw new UploadAlreadyCompleteAtCreateError(snapshot.id);
	for (let offset = 1000; offset < control.object.parts.length; offset += 1000) {
		const page = await client.admitObjects(
			snapshot.id,
			snapshot,
			[
				{
					clientObjectId: control.object.clientObjectId,
					manifestSha256: control.object.manifestSha256,
					partsPage: control.object.parts.slice(offset, offset + 1000)
				}
			],
			signal
		);
		if (page.objects[0].objectId !== objectId) throw uploadControlError("Upload object admission changed its binding");
	}
	const envelopes: UploadTargetEnvelope[] = [];
	for (let offset = 0; offset < control.object.parts.length; offset += 100) {
		const requested = control.object.parts.slice(offset, offset + 100).map((part) => ({ objectId, part }));
		const batch = await client.objectUrls(snapshot.id, snapshot, requested, signal);
		assertKnownSizeSeal(batch.snapshot, control);
		assertTransferAllowed(batch.snapshot);
		if (batch.targets.length !== requested.length) {
			if (batch.targets.length !== 0) throw new Error("Upload signing returned an incomplete part batch");
			const accepted = await client.completeV2(snapshot.id, snapshot, control.finalizationId);
			if (accepted.transferVerified) {
				assertKnownSizeSeal(accepted, control);
				throw new UploadAlreadyCompleteAtCreateError(snapshot.id);
			}
			throwUploadVerificationPending();
		}
		envelopes.push(...batch.targets);
	}
	return createUploadsMultipartUploadTarget({
		registryId: snapshot.id,
		attemptId: snapshot.attemptId,
		generation: snapshot.generation,
		takeGeneration: snapshot.takeGeneration,
		transport: "chunked-object",
		contractVersion: 2,
		objectUpload: bound,
		envelopes
	});
}
/** Read the current attempt and immutable seal before acting on its acceptance receipt. */
export async function readKnownSizeUploadReceipt(
	client: UploadsFacadeClient,
	identity: { registryId: string; attemptId: string; generation: number; takeGeneration: string },
	control: UploadKnownSizeControl
) {
	const snapshot = await client.getV2(identity.registryId, identity);
	assertKnownSizeSeal(snapshot, control);
	assertTransferAllowed(snapshot);
	return snapshot;
}

/** A successful return means durable ingest acceptance, independently of domain readiness. */
export async function completeKnownSizeUpload(
	client: UploadsFacadeClient,
	target: UploadsMultipartUploadTarget
): Promise<{ alreadyComplete: boolean }> {
	const control = parseUploadKnownSizeControl(target.objectUpload, true);
	if (target.contractVersion !== 2 || !target.takeGeneration)
		throw uploadControlError("Upload object completion requires its saved v2 fence");
	const fence = { attemptId: target.attemptId, generation: target.generation, takeGeneration: target.takeGeneration };
	const prior = await readKnownSizeUploadReceipt(client, { registryId: target.registryId, ...fence }, control);
	if (prior.transferVerified) return { alreadyComplete: true };
	const receipt = await client.completeObjects(target.registryId, fence, [control.objectId!]);
	if (receipt.objects[0].state === "held") {
		// A drain attempt may request recovery; its existing retry policy paces the next observation.
		await client.retryWork(target.registryId, fence, [control.objectId!]);
		throwUploadVerificationPending();
	}
	if (receipt.objects[0].state !== "verified") throwUploadVerificationPending();
	const accepted = await client.completeV2(target.registryId, fence, control.finalizationId);
	assertKnownSizeSeal(accepted, control);
	assertTransferAllowed(accepted);
	if (!accepted.transferVerified) throwUploadVerificationPending();
	return { alreadyComplete: false };
}

/** Share protocol choice and immutable planning; existing stores own the atomic identity check. */
export async function prepareKnownSizeUploadSource(input: {
	client: UploadsFacadeClient;
	scope: { kind: UploadsAnyCreateRequest["kind"]; conversationId?: string; organizationId?: string };
	row: UploadObjectControlRow;
	unbound: boolean;
	byteSize: number;
	contentType: string;
	chunkSize: number;
	totalParts: number;
	readPart(offset: number, byteSize: number): Promise<Blob>;
	commitChoice(choice: UploadObjectControlRow): Promise<UploadObjectControlRow>;
}): Promise<UploadKnownSizeControl | undefined> {
	const saved = readUploadKnownSizeControl(input.row);
	if (!saved && (!input.unbound || input.row.contractVersion === 1 || !input.row.protocolSelectionPending)) return undefined;
	if (
		!Number.isSafeInteger(input.chunkSize) ||
		input.chunkSize < 5 * 1024 ** 2 ||
		!Number.isSafeInteger(input.totalParts) ||
		input.totalParts < 1 ||
		Math.ceil(input.byteSize / input.chunkSize) !== input.totalParts
	)
		throw uploadControlError("Saved source grid is unavailable");
	if (saved) {
		if (
			saved.object.byteSize !== input.byteSize ||
			saved.object.contentType !== input.contentType ||
			saved.object.parts.some(
				(part, index) =>
					part.offset !== index * input.chunkSize || part.byteSize !== Math.min(input.chunkSize, input.byteSize - part.offset)
			)
		)
			throw uploadControlError("Saved upload object grid differs from its retained source");
		return saved;
	}
	const capabilities = await input.client.capabilities({ ...input.scope, sourceMode: "known-size" });
	const control = await planKnownSizeUploadSource({ ...input, kind: input.scope.kind, capabilities });
	if (!control) return readUploadKnownSizeControl(await input.commitChoice({ contractVersion: 1, protocolSelectionPending: false }));
	return readUploadKnownSizeControl(
		await input.commitChoice({ contractVersion: 2, objectUploadJson: JSON.stringify(control), protocolSelectionPending: false })
	);
}

/** Pure source planning. The existing host store must persist this choice and bytes before admission. */
export async function planKnownSizeUploadSource(input: {
	kind: UploadsAnyCreateRequest["kind"];
	capabilities: UploadCapabilities;
	byteSize: number;
	contentType: string;
	chunkSize: number;
	readPart(offset: number, byteSize: number): Promise<Blob>;
}): Promise<UploadKnownSizeControl | undefined> {
	const { capabilities } = input;
	if (
		capabilities.preferredContractVersion !== 2 ||
		!capabilities.v2.newUploadsAllowed ||
		!capabilities.v2.sourceModes.includes("known-size") ||
		!capabilities.v2.kinds.includes(input.kind)
	)
		return undefined;
	if (
		input.byteSize > capabilities.v2.domainByteLimit ||
		input.chunkSize > capabilities.v2.limits.maxPartBytes ||
		Math.ceil(input.byteSize / input.chunkSize) > capabilities.v2.limits.maxPartsPerObject
	)
		throw new Error("Upload source exceeds the server's object limits");
	return {
		contractVersion: 2,
		finalizationId: newUploadId(),
		object: await buildKnownSizeUploadObject({
			clientObjectId: newUploadId(),
			byteSize: input.byteSize,
			contentType: input.contentType,
			chunkSize: input.chunkSize,
			readPart: input.readPart
		})
	};
}

export async function refreshKnownSizeUploadUrls(
	client: UploadsFacadeClient,
	target: UploadsMultipartUploadTarget,
	partNumbers: readonly number[]
) {
	const control = parseUploadKnownSizeControl(target.objectUpload, true);
	if (!target.takeGeneration || target.contractVersion !== 2 || new Set(partNumbers).size !== partNumbers.length)
		throw uploadControlError("Upload object refresh has no exact binding");
	const fence = { attemptId: target.attemptId, generation: target.generation, takeGeneration: target.takeGeneration };
	const updates: { partNumber: number; url: string; envelope: UploadTargetEnvelope }[] = [];
	for (let offset = 0; offset < partNumbers.length; offset += 100) {
		const requested = partNumbers.slice(offset, offset + 100).map((ordinal) => {
			const part = control.object.parts[ordinal - 1];
			if (!part || part.ordinal !== ordinal) throw uploadControlError("Upload refresh requested an unknown part");
			return { objectId: control.objectId!, part };
		});
		const batch = await client.objectUrls(target.registryId, fence, requested);
		assertKnownSizeSeal(batch.snapshot, control);
		assertTransferAllowed(batch.snapshot);
		if (batch.targets.length !== requested.length) throwUploadVerificationPending();
		updates.push(...batch.targets.map((envelope) => ({ partNumber: envelope.ordinal, url: envelope.url, envelope })));
	}
	return updates;
}
export async function knownSizePartReceipt(client: UploadsFacadeClient, target: UploadsMultipartUploadTarget, ordinal: number) {
	const control = parseUploadKnownSizeControl(target.objectUpload, true);
	const part = control.object.parts[ordinal - 1];
	if (!target.takeGeneration || target.contractVersion !== 2 || !part || part.ordinal !== ordinal)
		throw uploadControlError("Upload part receipt has no exact binding");
	const value = await client.objectPartStatus(
		target.registryId,
		{ attemptId: target.attemptId, generation: target.generation, takeGeneration: target.takeGeneration },
		control.objectId!,
		part
	);
	assertKnownSizeSeal(value.snapshot, control);
	assertTransferAllowed(value.snapshot);
	if (!value.receipt.verified) return null;
	return {
		partNumber: ordinal,
		ordinal,
		etag: value.receipt.eTag,
		size: part.byteSize,
		targetId: `${target.attemptId}:${control.objectId}:${ordinal}`
	};
}

export function persistedKnownSizeUploadTarget(row: PersistedUploadTargetRow): UploadsMultipartUploadTarget | undefined {
	if (row.contractVersion !== 2 && row.objectUploadJson === undefined) return undefined;
	const target = persistedUploadTarget(row);
	if (!target) throw uploadControlError("Saved object upload lost its uploads family");
	return target;
}
